import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'
import {
  useWorkYard, useTasks, useCreateTask, useUpdateTask, useDeleteTask, useCopyTasks,
  useRoster, useMembers,
} from '../api/hooks'
import type { YardTask } from '@horsehub/shared'
import { roleAtLeast } from '@horsehub/shared'
import { Modal, Input, Textarea, Select, PageSpinner, EmptyState } from '../components/ui'
import { confirmDelete } from '../lib/confirm'
import { toast } from '../lib/toast'
import { errorMessage } from '../lib/errors'

/** YYYY-MM-DD in local time — a yard's "today" is the groom's today, not UTC's. */
function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function shiftDate(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number)
  return localDateKey(new Date(y, m - 1, d + days))
}

/**
 * The groom's daily list — the phone-first heart of the yard. Big tap targets,
 * one thumb, works in a muddy glove. Managers plan; everyone ticks off.
 */
export function FarmTasks() {
  const { t, locale } = useI18n()
  const { farmId, role, isLoading } = useWorkYard()
  const today = localDateKey(new Date())
  const [date, setDate] = useState(today)
  const { data: tasks, isLoading: tasksLoading } = useTasks(farmId, date, date)
  const update = useUpdateTask(farmId ?? '')
  const del = useDeleteTask(farmId ?? '')
  const copy = useCopyTasks(farmId ?? '')
  const [adding, setAdding] = useState(false)

  if (isLoading) return <PageSpinner />
  if (!farmId) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-16 text-center">
        <p className="text-lg">🏡</p>
        <p className="mt-2 font-semibold">{t('farm.dash.rosterEmpty')}</p>
        <Link to="/farm/onboarding" className="btn-primary mt-4 inline-block">{t('farm.onboard.title')}</Link>
      </main>
    )
  }

  const canPlan = !!role && roleAtLeast(role, 'manager')
  const done = (tasks ?? []).filter((x) => x.status === 'done').length
  const total = (tasks ?? []).length
  const dayLabel = date === today
    ? t('farm.tasks.today')
    : new Date(date + 'T12:00:00').toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })

  const copyYesterday = async () => {
    try {
      const res = await copy.mutateAsync({ from: shiftDate(date, -1), to: date })
      toast.success(t('farm.tasks.copied', { n: res.copied }))
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-5 py-8 pb-24">
      {/* Day switcher — big arrows, current day centred */}
      <div className="flex items-center justify-between gap-2">
        <Link to="/farm" className="btn-ghost !px-3 !py-2 text-sm">←</Link>
        <div className="flex items-center gap-1">
          <button onClick={() => setDate(shiftDate(date, -1))} aria-label="Previous day" className="grid h-11 w-11 place-items-center rounded-full text-xl hover:bg-brand-100 dark:hover:bg-brand-800">‹</button>
          <button onClick={() => setDate(today)} className="min-w-32 rounded-full px-4 py-2 text-center font-bold hover:bg-brand-100 dark:hover:bg-brand-800">
            {dayLabel}
          </button>
          <button onClick={() => setDate(shiftDate(date, 1))} aria-label="Next day" className="grid h-11 w-11 place-items-center rounded-full text-xl hover:bg-brand-100 dark:hover:bg-brand-800">›</button>
        </div>
        {canPlan ? (
          <button onClick={() => setAdding(true)} className="btn-primary !px-4 !py-2.5 text-sm">{t('farm.tasks.add')}</button>
        ) : (
          <span className="w-12" />
        )}
      </div>

      <h1 className="mt-4 text-2xl font-extrabold tracking-tight">{t('farm.tasks.title')}</h1>

      {/* Progress */}
      {total > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm font-semibold text-brand-700 dark:text-brand-200">
            <span>{t('farm.tasks.progress', { done, total })}</span>
            {done === total && <span>{t('farm.tasks.allDone')}</span>}
          </div>
          <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-brand-100 dark:bg-brand-800">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {/* Task list */}
      {tasksLoading ? (
        <PageSpinner />
      ) : total === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon="✅"
            title={t('farm.tasks.empty')}
            body={t('farm.tasks.emptyBody')}
            action={
              canPlan ? (
                <div className="flex flex-wrap justify-center gap-3">
                  <button onClick={() => setAdding(true)} className="btn-primary">{t('farm.tasks.add')}</button>
                  <button onClick={copyYesterday} disabled={copy.isPending} className="btn-ghost disabled:opacity-60">
                    {copy.isPending ? '…' : t('farm.tasks.copyYesterday')}
                  </button>
                </div>
              ) : undefined
            }
          />
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {(tasks ?? []).map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              canPlan={canPlan}
              onToggle={() =>
                update.mutate(
                  { date: task.date, tid: task.id, body: { status: task.status === 'done' ? 'todo' : 'done' } },
                  { onError: (e) => toast.error(errorMessage(e)) },
                )
              }
              onDelete={() => confirmDelete(t).then((ok) => ok && del.mutate({ date: task.date, tid: task.id }))}
            />
          ))}
        </ul>
      )}

      {adding && <AddTaskModal farmId={farmId} date={date} onClose={() => setAdding(false)} />}
    </main>
  )
}

function TaskRow({ task, canPlan, onToggle, onDelete }: {
  task: YardTask; canPlan: boolean; onToggle: () => void; onDelete: () => void
}) {
  const { t } = useI18n()
  const done = task.status === 'done'
  return (
    <li className={`card flex items-center gap-3 p-3 ${done ? 'opacity-70' : ''}`}>
      {/* The tick — the whole reason this page exists. 48px target. */}
      <button
        onClick={onToggle}
        aria-label={done ? t('farm.tasks.todo') : t('farm.tasks.done')}
        className={`grid h-12 w-12 shrink-0 place-items-center rounded-full border-2 text-xl transition ${
          done
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-brand-300 dark:border-brand-600 hover:border-emerald-500'
        }`}
      >
        {done ? '✓' : ''}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`font-semibold ${done ? 'line-through' : ''}`}>{task.title}</div>
        <div className="flex flex-wrap gap-x-2 text-sm text-brand-600 dark:text-brand-300">
          {task.horseName && <span>🐎 {task.horseName}</span>}
          {task.assignedToName && <span>👤 {task.assignedToName}</span>}
          {done && task.doneByName && <span>{t('farm.tasks.doneBy', { name: task.doneByName })}</span>}
        </div>
        {task.notes && <div className="mt-0.5 text-sm text-brand-600 dark:text-brand-300">{task.notes}</div>}
      </div>
      {canPlan && (
        <button onClick={onDelete} aria-label={t('farm.tasks.deleteTask')} className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-brand-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30">
          🗑
        </button>
      )}
    </li>
  )
}

function AddTaskModal({ farmId, date, onClose }: { farmId: string; date: string; onClose: () => void }) {
  const { t } = useI18n()
  const create = useCreateTask(farmId)
  const { data: roster } = useRoster(farmId)
  const { data: members } = useMembers(farmId)
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')
  const [horseId, setHorseId] = useState('')
  const [assignedToId, setAssignedToId] = useState('')
  const [err, setErr] = useState('')

  const staff = (members ?? []).filter((m) => m.role === 'manager' || m.role === 'groom')

  const save = async () => {
    if (!title.trim()) {
      setErr(t('errors.required', { fields: t('farm.tasks.taskName') }))
      return
    }
    setErr('')
    try {
      await create.mutateAsync({
        date,
        title: title.trim(),
        notes: notes.trim() || undefined,
        horseId: horseId || undefined,
        assignedToId: assignedToId || undefined,
      })
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  return (
    <Modal title={t('farm.tasks.addTitle')} onClose={onClose}>
      <div className="space-y-4">
        <Input label={t('farm.tasks.taskName')} value={title} onChange={setTitle} placeholder={t('farm.tasks.taskPlaceholder')} required />
        <Textarea label={t('farm.tasks.notes')} value={notes} onChange={setNotes} rows={2} />
        <Select
          label={t('farm.tasks.forHorse')}
          value={horseId}
          onChange={setHorseId}
          options={[
            { value: '', label: t('farm.tasks.wholeYard') },
            ...(roster ?? []).map((r) => ({ value: r.horseId, label: r.horseName })),
          ]}
        />
        {staff.length > 0 && (
          <Select
            label={t('farm.tasks.assignTo')}
            value={assignedToId}
            onChange={setAssignedToId}
            options={[
              { value: '', label: t('farm.tasks.anyone') },
              ...staff.map((m) => ({ value: m.userId, label: m.name })),
            ]}
          />
        )}
        {err && <p className="text-sm font-semibold text-red-600 dark:text-red-400">{err}</p>}
        <button onClick={save} disabled={create.isPending} className="btn-primary w-full disabled:opacity-60">
          {create.isPending ? t('common.save') + '…' : t('common.save')}
        </button>
      </div>
    </Modal>
  )
}
